<template>
  <div class="pl-4">
    <KanbanColumnHeader :column="column" />
    <div v-if="column.tasks.length === 0" class="empty-state">
      <CandidateCard />
    </div>
    <div class="overflow-y-auto max-h-[calc(100vh-160px)]">
      <Draggable
        :id="column.column_id"
        v-model="column.tasks"
        group="tasks"
        class="drag-area min-h-80"
        :chosen-class="column.locked ? 'dragging' : 'default'"
        :animation="150"
        :move="checkMove"
        @end="emit('set-drag-event', $event)"
        @change="handleTrigger($event)"
      >
        <div v-for="item in column.tasks" :key="item.id" class="task-item mb-2">
          <CandidateCard :item="item" :trigger="reorderTrigger" @handle-update="handleReorder" />
        </div>
      </Draggable>
      <Button
        v-if="column.pagination.total > column.tasks.length"
        variant="link"
        class="text-xs font-light text-gray-500"
        @click="emit('load-more', getLoadMoreParams())"
      >
        Load More
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { KanbanColumnData, FetchParams, UpdateParams } from '@/types/kanban';
import type { DragUpdateEvent, MovedEvent, ReorderTrigger } from '@/types/draggable.ts';
import { Button } from '@/components/ui/button';
import { VueDraggableNext as Draggable } from 'vue-draggable-next';
import KanbanColumnHeader from '@/components/shared/Kanban/KanbanColumnHeader.vue';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { hFetch } from '@/lib/hfetch';
import { toast } from 'vue-sonner';
import CandidateCard from '@/components/shared/Kanban/CandidateCard.vue';

const blankTrigger = <ReorderTrigger>{ column_id: '', id: 0 };
const reorderTrigger = ref<ReorderTrigger>(blankTrigger);
const updateEvent = ref<MovedEvent | null>(null);
const loadingSpinnerStore = useLoadingSpinnerStore();

const emit = defineEmits(['update:revert-drag', 'set-drag-event', 'load-more', 'update:column-totals']);

const { column, baseUrl } = defineProps<{
  baseUrl: string;
  column: KanbanColumnData;
}>();

function handleTrigger(event: DragUpdateEvent) {
  if (event.added) {
    updateEvent.value = event.added;
    reorderTrigger.value = { column_id: column.column_id, id: event.added.element.id };
  } else if (event.moved) {
    updateEvent.value = event.moved;
    handleReorder('update');
  }
}

type DragResult = 'revert' | 'update' | 'done';

async function handleReorder(dragResult: DragResult, message?: string): Promise<void> {
  reorderTrigger.value = blankTrigger;
  if (dragResult === 'revert') {
    emit('update:revert-drag', message);
  } else if (dragResult === 'done') {
    updateEvent.value = null;
    emit('set-drag-event', null);
  } else if (updateEvent.value && updateEvent.value.newIndex !== undefined) {
    loadingSpinnerStore.showLocalLoadingSpinner();
    const index = calculateIndex(updateEvent.value.newIndex);

    const response = await hFetch<{ [key: string]: number }, UpdateParams>({
      url: `${baseUrl}/${updateEvent.value.element.id}`,
      method: 'PUT',
      payload: { column_id: column.column_id, order_index: index },
    });

    if (response.status === 'success') {
      updateEvent.value = null;
      emit('set-drag-event', null);
      emit('update:column-totals', response.data.totals);
      loadingSpinnerStore.hideLocalLoadingSpinner();
      toast.success(`Updated successfully`, {
        description: `Your item was successfully moved.`,
      });
    } else {
      emit('update:revert-drag', message);
      loadingSpinnerStore.hideLocalLoadingSpinner();
      toast.error(`Column update error`, {
        description: response.messages.join(', '),
      });
    }
  }
}

function calculateIndex(new_index: number): number {
  const self = column.tasks[new_index]?.order_index;
  const before_neighbor = column.tasks[new_index - 1]?.order_index;
  const after_neighbor = column.tasks[new_index + 1]?.order_index;

  if (self <= before_neighbor) {
    return before_neighbor;
  } else if (after_neighbor) {
    return after_neighbor - 1;
  } else {
    return new_index + 1;
  }
}

function checkMove(event: { from: string; to: string }) {
  return !column.locked || event.from === event.to;
}

function getLoadMoreParams(): FetchParams {
  return {
    column_id: column.column_id,
    page: column.pagination.page + 1,
    last_index: column.tasks[column.tasks.length - 1]?.order_index,
  };
}
</script>

<style scoped>
@reference "tailwindcss";
.dragging {
  @apply border-red-500 rounded-md border-2 m-0 p-0;
}
</style>
